import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { ImportedModelUnitContext } from './types'
import { asRecord } from './utils'
import {
  readPositiveNumberMetadataValue,
  readStringMetadataValue,
} from './metadata-reader'

/**
 * 导入模型缺省长度单位到米的换算比例。
 *
 * 设计说明：
 * 1. 后端文档约定未显式指定 `scaleFactorToMeter` 时默认按米解释；
 * 2. 该常量只描述导出结果单位上下文，不代表源 STEP 的原始单位；
 * 3. hydration 阶段统一消费该值，避免按零件或装配体类型猜测缩放比例。
 */
export const DEFAULT_IMPORTED_MODEL_SCALE_FACTOR_TO_METER = 1

/**
 * 单位名称到米制比例的规范表。
 *
 * 设计说明：
 * 1. 表内只收录后端文档列出的常见单位，避免引入不透明的自动推断；
 * 2. 当 `scaleFactorToMeter` 缺失但 `unit` 明确存在时，可用该表兜底；
 * 3. 若两者都缺失，则回到协议默认值 1。
 */
const SCALE_FACTOR_TO_METER_BY_UNIT: Record<string, number> = {
  m: 1,
  meter: 1,
  meters: 1,
  metre: 1,
  metres: 1,
  cm: 0.01,
  centimeter: 0.01,
  centimeters: 0.01,
  centimetre: 0.01,
  centimetres: 0.01,
  mm: 0.001,
  millimeter: 0.001,
  millimeters: 0.001,
  millimetre: 0.001,
  millimetres: 0.001,
  inch: 0.0254,
  inches: 0.0254,
  in: 0.0254,
}

/**
 * 克隆单位上下文，防止 loader 之间共享可变引用。
 *
 * @param context 待克隆的单位上下文。
 * @returns 克隆后的单位上下文。
 */
export function cloneImportedModelUnitContext(
  context: ImportedModelUnitContext | undefined,
): ImportedModelUnitContext | undefined {
  if (!context) {
    return undefined
  }

  return {
    unit: context.unit,
    scaleFactorToMeter: context.scaleFactorToMeter,
    source: context.source,
  }
}

/**
 * 规范化导出单位名称。
 *
 * @param value 原始单位名称。
 * @returns 小写单位名称；不存在时返回 `undefined`。
 */
function normalizeUnit(value: string | undefined): string | undefined {
  const unit = value?.trim().toLowerCase()
  return unit && unit.length > 0 ? unit : undefined
}

/**
 * 根据单位名称推导到米的换算比例。
 *
 * @param unit 规范化后的单位名称。
 * @returns 到米的换算比例；未知单位返回 `undefined`。
 */
function resolveScaleFactorFromUnit(unit: string | undefined): number | undefined {
  return unit ? SCALE_FACTOR_TO_METER_BY_UNIT[unit] : undefined
}

/**
 * 从 glTF parser JSON 中读取导入模型单位上下文。
 *
 * 设计说明：
 * 1. 后端文档把 `asset.extras.step.unit` 与 `extras.stepMetadata.scaleFactorToMeter`
 *    定义为最终导出单位，不应再透传源 STEP 原始单位；
 * 2. `scaleFactorToMeter` 是把当前导出单位转换到 viewer 米制渲染空间的权威比例；
 * 3. 若比例缺失但单位明确，使用单位表兜底；若全部缺失，按协议默认米制。
 *
 * @param parserJson glTF parser 原始 JSON。
 * @returns 导入模型单位上下文。
 */
export function resolveImportedModelUnitContextFromParserJson(parserJson: unknown): ImportedModelUnitContext {
  const parserRecord = asRecord(parserJson)
  const assetRecord = asRecord(parserRecord?.asset)
  const assetExtrasRecord = asRecord(assetRecord?.extras)
  const assetStepRecord = asRecord(assetExtrasRecord?.step)
  const rootExtrasRecord = asRecord(parserRecord?.extras)
  const stepMetadataRecords = [
    asRecord(rootExtrasRecord?.stepMetadata),
    asRecord(parserRecord?.stepMetadata),
    asRecord(assetExtrasRecord?.stepMetadata),
  ]

  const explicitUnit = normalizeUnit(readStringMetadataValue(
    [assetStepRecord, ...stepMetadataRecords, rootExtrasRecord, assetExtrasRecord],
    ['unit', 'lengthUnit'],
  ))
  const unit = explicitUnit ?? 'm'
  const explicitScaleFactor = readPositiveNumberMetadataValue(
    [...stepMetadataRecords, assetStepRecord, rootExtrasRecord, assetExtrasRecord],
    ['scaleFactorToMeter', 'scaleToMeters', 'scaleToMeter'],
  )
  const scaleFactorFromUnit = resolveScaleFactorFromUnit(explicitUnit)
  const scaleFactorToMeter = explicitScaleFactor
    ?? scaleFactorFromUnit
    ?? DEFAULT_IMPORTED_MODEL_SCALE_FACTOR_TO_METER

  return {
    unit,
    scaleFactorToMeter,
    source: explicitScaleFactor !== undefined
      ? 'scaleFactorToMeter'
      : (scaleFactorFromUnit !== undefined ? 'unit' : 'default'),
  }
}

/**
 * 从 GLTFLoader 结果中读取导入模型单位上下文。
 *
 * @param gltf GLTFLoader 解析结果。
 * @returns 导入模型单位上下文。
 */
export function resolveImportedModelUnitContextFromGltf(gltf: GLTF): ImportedModelUnitContext {
  const parserJson = (gltf.parser as unknown as { json?: unknown } | undefined)?.json
  return resolveImportedModelUnitContextFromParserJson(parserJson)
}
